import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Switch,
  FormControlLabel,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useColorTheme } from "context/ColorThemeContext";
import CustomTextField from "components/TextField";
import DragDropZone from "components/DragDropZone";
import CustomSelect from "components/CustomSelect";
import { fetchDesignSampleCreate, fetchDesignSampleUpdate } from "api/designSampleService";
import { getImageUrl } from "api/AttachmentAPI";
import { fetchAllTemplates } from "api/templateService";

function AddDesignSample({ open, onClose, editMode, initialData, refreshDataAfterChange }) {
  const colors = useColorTheme();
  const [form, setForm] = useState({
    name: "",
    displayOrder: 1,
    status: true,
    image: null,
    templateId: "",
  });
  const [errors, setErrors] = useState({});
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    const loadTemplates = async () => {
      const options = await fetchAllTemplates();
      setTemplates(options);
    };
    loadTemplates();
  }, []);

  useEffect(() => {
    const loadData = async () => {
      if (editMode && initialData) {
        let image = initialData.imageUrl || null;
        if (initialData.attachment?.encryptedId) {
          try {
            image = await getImageUrl(
              `/api/attachments/downloadFile/${initialData.attachment.encryptedId}`
            );
          } catch (e) {
            console.error("Lỗi tải ảnh:", e);
          }
        }
        setForm({
          name: initialData.designSampleName,
          displayOrder: initialData.displayOrder || 1,
          status: initialData.status === 1,
          image,
          templateId: initialData.templateId || "",
        });
      } else {
        setForm({ name: "", displayOrder: 1, status: true, image: null, templateId: "" });
      }
      setErrors({});
    };
    loadData();
  }, [editMode, initialData]);

  const handleClose = () => {
    setForm({ name: "", displayOrder: 1, status: true, image: null, templateId: "" });
    setErrors({ name: "", displayOrder: "", image: "" });
    onClose();
  };

  const validate = () => {
    const errs = {
      name: form.name.trim() ? "" : "Tên mẫu là bắt buộc",
      displayOrder: form.displayOrder >= 1 ? "" : "Thứ tự hiển thị phải ≥ 1",
      image: form.image ? "" : "Ảnh là bắt buộc",
      templateId: form.templateId ? "" : "Chọn danh mục sản phẩm",
    };
    setErrors(errs);
    return Object.values(errs).every((v) => !v);
  };

  const handleSave = async () => {
    if (!validate()) return;
    const payload = new FormData();
    payload.append("designSampleCode", "");
    payload.append("designSampleName", form.name.trim());
    payload.append("displayOrder", form.displayOrder);
    payload.append("status", form.status ? 1 : 0);
    payload.append("templateId", form.templateId);

    if (form.image) {
      const file =
        typeof form.image === "string"
          ? new File([await (await fetch(form.image)).blob()], "design_sample.jpg")
          : form.image;
      payload.append("image", file);
    }

    let result;
    if (editMode && initialData.designSampleId) {
      payload.append("designSampleId", initialData.designSampleId);
      result = await fetchDesignSampleUpdate(initialData.designSampleId, payload);
    } else {
      result = await fetchDesignSampleCreate(payload);
    }

    if (result.success) {
      Swal.fire({
        icon: "success",
        title: editMode ? "Cập nhật thành công" : "Thêm mới thành công",
        timer: 1500,
      });
      refreshDataAfterChange();
      handleClose();
    } else {
      Swal.fire({ icon: "error", title: "Lỗi", text: result.data?.message || "Có lỗi xảy ra" });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          p: 3,
          background: colors.popupBackground || "#f2f2f2",
        },
      }}
    >
      <DialogTitle sx={{ textAlign: "center", fontWeight: "bold", fontSize: "24px" }}>
        {editMode ? "Chỉnh sửa mẫu" : "Thêm mới mẫu"}
        <IconButton onClick={handleClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <CustomSelect
          label="Chọn danh mục sản phẩm"
          value={form.templateId}
          options={templates}
          onChange={(e) => setForm((prev) => ({ ...prev, templateId: e.target.value }))}
          placeholder="-- Chọn danh mục --"
          error={!!errors.templateId}
          helperText={errors.templateId}
          required
        />

        <CustomTextField
          label="Tên mẫu"
          value={form.name}
          onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
          required
          error={!!errors.name}
          helperText={errors.name}
        />

        <CustomTextField
          label="Số thứ tự hiển thị"
          type="number"
          value={form.displayOrder}
          onChange={(e) => setForm((prev) => ({ ...prev, displayOrder: +e.target.value }))}
          required
          error={!!errors.displayOrder}
          helperText={errors.displayOrder}
        />

        <FormControlLabel
          sx={{ mb: 2, ml: 0 }}
          control={
            <Switch
              checked={form.status}
              onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.checked }))}
            />
          }
          label="Kích hoạt"
        />

        <DragDropZone
          label="Ảnh"
          imagePreview={form.image}
          onDrop={(file) => setForm((prev) => ({ ...prev, image: file }))}
        />

        {errors.image && (
          <Typography color="error" fontSize="0.85rem" mt={0.5} ml={1.5}>
            {errors.image}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: "center" }}>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{
            backgroundColor: colors.buttonBackground || "#2196F3",
            color: colors.popupText || "white",
            borderRadius: "8px",
            paddingX: 4,
            paddingY: 1,
            textTransform: "none",
            fontWeight: 500,
            fontSize: "15px",
            "&:hover": {
              backgroundColor: "#1976D2",
            },
          }}
        >
          {editMode ? "Cập nhật" : "Thêm mới"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AddDesignSample.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  editMode: PropTypes.bool,
  initialData: PropTypes.oneOfType([PropTypes.object, PropTypes.oneOf([null])]),
  refreshDataAfterChange: PropTypes.func.isRequired,
};

AddDesignSample.defaultProps = {
  editMode: false,
  initialData: null,
};

export default AddDesignSample;
